import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { ApiError, request } from '../lib/api-client.ts';
import { formatJst } from '../lib/format.ts';
import { ScoreTable } from './ranking/ScoreTable.tsx';
import { PreviewTable } from './ranking/PreviewTable.tsx';
import type {
  PreviewRankingResponse,
  ScoreRankingResponse,
} from '../types/ranking.ts';

/**
 * 業種ランキング表示。
 *
 * - まず本番 `/api/industry/ranking`（scores テーブル）を取得
 * - scores が空なら `/api/industry/ranking-preview`（直近 signals の集計）にフォールバック
 * - preview も空なら「データ未投入」表示
 *
 * AbortError（unmount/再 mount による fetch キャンセル）は error 状態に流さない。
 */

type Status =
  | { kind: 'loading' }
  | { kind: 'scores'; data: ScoreRankingResponse }
  | { kind: 'preview'; data: PreviewRankingResponse }
  | { kind: 'error'; message: string }
  | { kind: 'empty'; updated_at: string | null };

type Props = {
  refreshKey: number;
};

const isAbortError = (err: unknown): boolean =>
  err instanceof DOMException && err.name === 'AbortError';

export const IndustryRanking = ({ refreshKey }: Props): React.JSX.Element => {
  const [status, setStatus] = useState<Status>({ kind: 'loading' });
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    const controller = new AbortController();

    const load = async (): Promise<void> => {
      try {
        const scores = await request<ScoreRankingResponse>('/api/industry/ranking', {
          signal: controller.signal,
        });
        if (!mountedRef.current) return;
        if (scores.rows.length > 0) {
          setStatus({ kind: 'scores', data: scores });
          return;
        }
        const preview = await request<PreviewRankingResponse>('/api/industry/ranking-preview', {
          signal: controller.signal,
        });
        if (!mountedRef.current) return;
        if (preview.rows.length === 0) {
          setStatus({ kind: 'empty', updated_at: preview.data_updated_at });
          return;
        }
        setStatus({ kind: 'preview', data: preview });
      } catch (err) {
        if (!mountedRef.current || isAbortError(err)) return;
        if (err instanceof ApiError && err.code === 'not_found') {
          setStatus({ kind: 'empty', updated_at: null });
          return;
        }
        const message = err instanceof Error ? err.message : 'ランキング取得に失敗';
        setStatus({ kind: 'error', message });
      }
    };

    void load();

    return () => {
      mountedRef.current = false;
      controller.abort();
    };
  }, [refreshKey]);

  return (
    <section style={sectionStyle}>
      <h2 style={headStyle}>業種ランキング</h2>
      {renderContent(status)}
    </section>
  );
};

const renderContent = (status: Status): React.JSX.Element => {
  if (status.kind === 'loading') return <p style={mutedStyle}>読み込み中…</p>;
  if (status.kind === 'error') return <p style={errorStyle}>{status.message}</p>;
  if (status.kind === 'empty') {
    return (
      <>
        <p style={mutedStyle}>まだデータがありません。データソースを収集してから統合スコアを計算してください。</p>
        <small style={mutedStyle}>更新: {formatJst(status.updated_at)}</small>
      </>
    );
  }
  if (status.kind === 'scores') return <ScoreTable data={status.data} />;
  return <PreviewTable data={status.data} />;
};

const sectionStyle: React.CSSProperties = {
  width: '100%',
  border: '1px solid #e0e0e0',
  borderRadius: 6,
  background: '#fff',
};
const headStyle: React.CSSProperties = {
  margin: 0,
  padding: '12px 16px',
  fontSize: 16,
  borderBottom: '1px solid #eee',
  background: '#f7f7f7',
};
const mutedStyle: React.CSSProperties = { color: '#888', fontSize: 13, padding: '12px 16px' };
const errorStyle: React.CSSProperties = { color: '#c0392b', fontSize: 13, padding: '12px 16px' };
